/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidEmail } from '../../utils/utils';
import contactDetailsFormStyles from './contact-details-form.styles';

export type ContactDetailsFormState = {
    phone: string;
    email: string;
    isInvalid: { phone: boolean; email: boolean };
};

type ContactDetailsFormProps = {
    defaultPhone?: string;
    defaultEmail?: string;
    onFormChange: (state: ContactDetailsFormState) => void;
    showPhoneDescription?: boolean;
    showEmailDescription?: boolean;
    requiredPhone?: boolean;
};

export const ContactDetailsForm = (props: ContactDetailsFormProps) => {
    const {
        defaultPhone = '',
        defaultEmail = '',
        onFormChange,
        showPhoneDescription = true,
        showEmailDescription = false,
        requiredPhone = true,
    } = props;

    const intl = useIntl();
    const [phone, setPhone] = useState(defaultPhone);
    const [email, setEmail] = useState(defaultEmail);
    const [isInvalid, setIsInvalid] = useState({ phone: false, email: false });

    useEffect(() => {
        setPhone(defaultPhone);
        setEmail(defaultEmail);
    }, [defaultPhone, defaultEmail]);

    const notifyFormChange = useCallback(
        (
            nextPhone: string,
            nextEmail: string,
            nextIsInvalid: { phone: boolean; email: boolean }
        ) => {
            onFormChange({
                phone: nextPhone,
                email: nextEmail,
                isInvalid: nextIsInvalid,
            });
        },
        [onFormChange]
    );

    useEffect(() => {
        notifyFormChange(phone, email, isInvalid);
    }, [phone, email, isInvalid, notifyFormChange]);

    const validatePhone = useCallback(() => {
        setIsInvalid(prev => ({
            ...prev,
            phone: !isValidPhoneNumber((phone ?? '') as string),
        }));
    }, [phone]);

    const validateEmail = useCallback(() => {
        setIsInvalid(prev => ({
            ...prev,
            email: !isValidEmail(email) && email.length > 0,
        }));
    }, [email]);

    const changePhoneHandler = useCallback((value: string | undefined) => {
        setPhone((value ?? '') as string);
        setIsInvalid(prev => ({ ...prev, phone: false }));
    }, []);

    const changeEmailHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        setIsInvalid(prev => ({ ...prev, email: false }));
    }, []);

    return (
        <>
            <div css={contactDetailsFormStyles.inputWrapper}>
                <h4 css={contactDetailsFormStyles.containerTitle}>
                    {intl.formatMessage({ id: 'phone' })}
                    {/* {requiredPhone && <span css={contactDetailsFormStyles.required}>*</span>} */}
                </h4>
                <form css={contactDetailsFormStyles.form} onSubmit={e => e.preventDefault()}>
                    <PhoneInput
                        value={phone}
                        onChange={changePhoneHandler}
                        inputMode="numeric"
                        css={contactDetailsFormStyles.input}
                        international
                        onBlur={validatePhone}
                    />
                </form>
                {isInvalid.phone && (
                    <span css={contactDetailsFormStyles.errorMessage}>
                        {intl.formatMessage({ id: 'invalidPhone' })}
                    </span>
                )}
                {showPhoneDescription && (
                    <p css={contactDetailsFormStyles.description}>
                        {intl.formatMessage({ id: 'enterPhoneInfo' })}
                    </p>
                )}
            </div>
            <div css={contactDetailsFormStyles.inputWrapper}>
                <h4 css={contactDetailsFormStyles.containerTitle}>
                    {intl.formatMessage({ id: 'email' })}
                    <span css={contactDetailsFormStyles.optionalText}>
                        ({intl.formatMessage({ id: 'optional' })})
                    </span>
                </h4>
                <form
                    css={[contactDetailsFormStyles.form, contactDetailsFormStyles.formEmail]}
                    onSubmit={e => e.preventDefault()}>
                    <input
                        value={email}
                        onChange={changeEmailHandler}
                        css={contactDetailsFormStyles.input}
                        type="email"
                        inputMode="email"
                        placeholder={intl.formatMessage({ id: 'emailInputPlaceholder' })}
                        onBlur={validateEmail}
                    />
                </form>
                {isInvalid.email && (
                    <span css={contactDetailsFormStyles.errorMessage}>
                        {intl.formatMessage({ id: 'invalidEmail' })}
                    </span>
                )}
                {showEmailDescription && (
                    <>
                        <p css={contactDetailsFormStyles.description}>
                            {intl.formatMessage({ id: 'contacts.description.1' })}
                        </p>
                        <p css={contactDetailsFormStyles.description}>
                            {intl.formatMessage({ id: 'contacts.description.2' })}
                        </p>
                    </>
                )}
            </div>
        </>
    );
};
